import { type ClassValue, clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";
import resolveConfig from "tailwindcss/resolveConfig";

import TEMPLATE_VARIABLES from "common/constants/templateVariables";
import type {
  LeadsSearchParams,
  LinkedInProfile,
  PricingPeriodInfo,
  PricingPeriodUnit,
  SelectedFacetParam,
} from "common/types";

import tailwindConfig from "../../../tailwind.config";

import type { JSONContent } from "@tiptap/react";
import type {
  BaseIssue,
  ErrorMessage,
  MaxLengthAction,
  MaxLengthIssue,
  PipeItem,
  SchemaWithPipe,
  StringSchema,
} from "valibot";

type Entries<T> = { [K in keyof T]: [K, T[K]] }[keyof T];
export function typedObjectEntries<T extends object>(t: T): Entries<T>[] {
  return Object.entries(t) as Entries<T>[];
}
export function typedObjectKeys<T extends object>(t: T): Array<keyof T> {
  return Object.keys(t) as Array<keyof T>;
}
export function getDayName(dayNumber: number) {
  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  return dayNames[dayNumber];
}
export function getMonthName(monthNumber: number, shorten: boolean = false) {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return shorten
    ? monthNames[monthNumber].slice(0, 3)
    : monthNames[monthNumber];
}
export function addNumberOrdinal(number: number) {
  const numberAsString = number.toString();
  const lastTwoDigits = numberAsString.slice(-2);

  // Special cases: 11, 12, 13 always use "th"
  if (["11", "12", "13"].includes(lastTwoDigits)) {
    return `${number}th`;
  }

  const lastNumber = Number(numberAsString[numberAsString.length - 1]);

  let ordinal: string;

  switch (lastNumber) {
    case 1:
      ordinal = "st";
      break;
    case 2:
      ordinal = "nd";
      break;
    case 3:
      ordinal = "rd";
      break;
    default:
      ordinal = "th";
  }

  return `${number}${ordinal}`;
}
export function timeSince(unixMilliseconds: number) {
  const msPerMinute = 60 * 1000;
  const msPerHour = msPerMinute * 60;
  const msPerDay = msPerHour * 24;
  const msPerMonth = msPerDay * 30;
  const msPerYear = msPerDay * 365;

  const elapsed = Date.now() - unixMilliseconds;

  if (elapsed < msPerMinute) {
    return "Just now";
  }
  if (elapsed < msPerHour) {
    const time = Math.round(elapsed / msPerMinute);
    return `${time} ${time > 1 ? "minutes" : "minute"} ago`;
  }
  if (elapsed < msPerDay) {
    const time = Math.round(elapsed / msPerHour);
    return `${time} ${time > 1 ? "hours" : "hour"} ago`;
  }
  if (elapsed < msPerMonth) {
    const time = Math.round(elapsed / msPerDay);
    return `${time} ${time > 1 ? "days" : "day"} ago`;
  }
  if (elapsed < msPerYear) {
    const time = Math.round(elapsed / msPerMonth);
    return `${time} ${time > 1 ? "months" : "month"} ago`;
  }
  const time = Math.round(elapsed / msPerYear);
  return `${time} ${time > 1 ? "years" : "year"} ago`;
}
export function formatTime(unixMilliseconds: number) {
  const date = new Date(unixMilliseconds);
  let hours: string | number = date.getHours();
  let minutes: string | number = date.getMinutes();

  hours = hours > 9 ? hours : `0${hours}`;
  minutes = minutes > 9 ? minutes : `0${minutes}`;

  return `${hours}:${minutes}`;
}
// Helper function to format time in 12-hour format with AM/PM
export function formatTimestamp(unixMilliseconds: number) {
  const date = new Date(unixMilliseconds);
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";

  hours %= 12;
  hours = hours || 12; // the hour "0" should be "12"
  return `${hours}:${minutes} ${ampm}`;
}
export function formatDate(unixMilliseconds: number) {
  const dateObject = new Date(unixMilliseconds);
  const date = dateObject.getDate();
  const month = dateObject.getMonth() + 1;
  return `${date > 9 ? date : `0${date}`}.${
    month > 9 ? month : `0${month}`
  }.${dateObject.getFullYear()}`;
}
/* 0 -> 9,999 -- 6754
10,000 -> 99,999 -- 57.2k
100,000 -> 999,999 -- 320k
1,000,000 -> 9,999,999 -- 2.35M
10,000,000 -> 99,999,999 -- 22.5M
100,000,000+ -- 212M */
export function formatNumber(number: number) {
  if (number < 10000) {
    return number.toString();
  }
  if (number < 1000000) {
    return `${Number((number / 1000).toFixed(2))}k`;
  }
  return `${Number((number / 1000000).toFixed(2))}M`;
}
// returns location | occupation or occupation if location is not defined
export function getProfileSubtitle(
  profile: Pick<LinkedInProfile, "location" | "occupation">,
) {
  if (profile.location) {
    return `${profile.location.name} | ${profile.occupation}`;
  }
  return profile.occupation;
}
export function formatLastUpdateAt(unixTimestamp: number) {
  if (Date.now() - unixTimestamp < 60 * 1000) {
    return "just now";
  }
  const today = new Date();
  today.setHours(0, 0, 0);

  if (unixTimestamp > today.getTime()) {
    return formatTimestamp(unixTimestamp);
  }
  const date = new Date(unixTimestamp);
  return `${getMonthName(date.getMonth(), true)} ${date.getDate()}`;
}
export function getSchemaMaxLength(
  schema: SchemaWithPipe<
    [StringSchema<string>, ...PipeItem<unknown, unknown, BaseIssue<unknown>>[]]
  >,
) {
  for (let i = 0; i < schema.pipe.length; i += 1) {
    const validation = schema.pipe[i];
    if (validation.type === "max_length") {
      return (
        validation as MaxLengthAction<
          string,
          number,
          ErrorMessage<MaxLengthIssue<string, number>>
        >
      ).requirement;
    }
  }
  throw Error(`No max_length rule found in schema: ${schema}`);
}
// returns false for preview and development environments
export function isProductionEnvironment() {
  return ["production", "staging"].includes(import.meta.env.MODE);
}
export const tailwindTheme = resolveConfig(tailwindConfig).theme;

const twMerge = extendTailwindMerge({
  override: {
    classGroups: {
      "font-size": typedObjectKeys(tailwindTheme.fontSize).map(
        (variant) => `text-${variant}`,
      ),
      z: typedObjectKeys(tailwindTheme.zIndex).map((variant) => `z-${variant}`),
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatPrice(price: number) {
  return price.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    trailingZeroDisplay: "stripIfInteger",
  });
}

export function formatPricingPeriodInfo(pricingPeriodInfo: PricingPeriodInfo) {
  const { period, period_unit } = pricingPeriodInfo;

  if (period === 1) {
    return period_unit;
  }
  return `${period} ${period_unit}s`;
}

export function formatPeriodUnitLabel(periodUnit: PricingPeriodUnit) {
  return periodUnit === "month" ? "mo" : "yr";
}

function getDaySuffix(day: number) {
  if (day >= 11 && day <= 13) return "th";

  const suffixes: { [key: number]: string } = { 1: "st", 2: "nd", 3: "rd" };
  return suffixes[day % 10] || "th";
}

export function formatShortDate(unixMilliseconds: number) {
  const date = new Date(unixMilliseconds);
  const day = date.getDate();
  const suffix = getDaySuffix(day);

  const formattedDate = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

  return formattedDate.replace(day.toString(), `${day}${suffix}`);
}

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

export function getCountryName(countryCode: string) {
  return regionNames.of(countryCode.toUpperCase());
}

export function parseTemplateText(
  text: string,
  customVariables: { name: string; value: string }[],
  aiDescriptors: Record<string, string>,
) {
  const mentionPattern = /\{\{(.*?)(?:\?(.*?))?\}\}/g;
  const lines = text.split("\n");
  const content: JSONContent[] = [];

  lines.forEach((line) => {
    const paragraph: JSONContent = { type: "paragraph", content: [] };
    let lastIndex = 0;

    line.replace(mentionPattern, (match, p1, p2, offset) => {
      const variableName = p1.trim();
      const fallbackValue = p2 ? p2.trim() : "";

      if (offset > lastIndex) {
        paragraph.content!.push({
          type: "text",
          text: line.slice(lastIndex, offset),
        });
      }

      // AI Variable
      if (variableName.startsWith("AI.")) {
        const key = variableName.replace("AI.", "");
        const description = aiDescriptors[key] || "";

        paragraph.content!.push({
          type: "aiVariable",
          attrs: {
            id: key,
            description,
          },
        });
      } else {
        // Regular or custom variable
        const variable = [...customVariables, ...TEMPLATE_VARIABLES].find(
          ({ value }) => value === `{{${variableName}}}`,
        );

        // skip if not found
        if (!variable) {
          lastIndex = offset;
          return match;
        }

        paragraph.content!.push({
          type: "variable",
          attrs: {
            id: variable.value,
            label: variable.name,
            fallback: fallbackValue || "",
            isCustomVar: !!customVariables.find(
              ({ value }) => value === variable.value,
            ),
          },
        });
      }

      lastIndex = offset + match.length;
    });

    // Add remaining text after last match
    if (lastIndex < line.length) {
      paragraph.content!.push({ type: "text", text: line.slice(lastIndex) });
    }

    // Ensure empty paragraphs are included
    if (paragraph.content.length > 0) {
      content.push(paragraph);
    } else {
      content.push({ type: "paragraph" });
    }
  });

  return { type: "doc", content };
}

export function stringifyTemplateContent(doc: JSONContent): string {
  return doc.content
    .map(({ content }) =>
      (content ?? [])
        .map((node) => {
          if (node.type === "variable") {
            return `${node.attrs.fallback ? `${node.attrs.id.slice(0, -2)}?${node.attrs.fallback}}}` : node.attrs.id}`;
          }

          if (node.type === "aiVariable") {
            return `{{AI.${node.attrs.id}}}`;
          }

          return node.text;
        })
        .join(""),
    )
    .join("\n");
}

export function extractAiDescriptors(nodes: JSONContent[]) {
  const aiDescriptions: Record<string, string> = {};

  if (!Array.isArray(nodes)) {
    return aiDescriptions;
  }

  nodes.forEach((node) => {
    if (node && Array.isArray(node.content)) {
      node.content.forEach((item) => {
        if (item && item.type === "aiVariable") {
          aiDescriptions[item.attrs.id] = item.attrs.description;
        }
      });
    }
  });

  return aiDescriptions;
}

export function decodeProxyUrl(proxyUrl: string) {
  const [protocol, address] = proxyUrl.split("://");

  if (address.includes("@")) {
    const [auth, actualAddress] = address.split("@");
    const [username, password] = auth.split(":");

    return { protocol, username, password, address: actualAddress };
  }
  return { protocol, address };
}

// proxy is either a custom proxy id or a country code
export function getProxyConfigPayload(selectedProxy: string) {
  if (selectedProxy.length === 2) {
    return {
      "location.countryISO": {
        $eq: selectedProxy,
      },
      "provider.name": {
        $eq: "nord",
      },
    };
  }
  return { custom_proxy_id: selectedProxy };
}
// example: 1-10 of 15, {from}-{to} of {totalItemCount}
export function getPaginationInfoText(
  selectedIndex: number,
  pageSize: number,
  totalItemCount: number,
) {
  const pagesCount = Math.ceil(totalItemCount / pageSize);

  const from = selectedIndex * pageSize + 1;
  const to =
    selectedIndex * pageSize +
    (selectedIndex === pagesCount - 1
      ? totalItemCount - Math.floor(totalItemCount / pageSize) * pageSize
      : pageSize);

  return `${from}-${to} of ${totalItemCount}`;
}

export function trackMetaPixel(
  type: "track" | "trackCustom",
  event: string,
  params?: Record<string, unknown>,
) {
  if (typeof window.fbq === "function") {
    window.fbq(type, event, params);
  }
}
// returns parsed search params for server requests
export function parseLeadsSearchParams(
  searchParams: LeadsSearchParams<SelectedFacetParam>,
) {
  return typedObjectEntries(searchParams).reduce(
    (accumulator, [key, value]) => {
      if (key === "keywords") {
        accumulator[key] = value;
      } else if (key === "favorite") {
        accumulator[key] = value;
      } else {
        accumulator[key] = value.flatMap(({ id }) => id.split(","));
      }
      return accumulator;
    },
    {} as LeadsSearchParams<string>,
  );
}
